import { FunctionsHttpError } from '@supabase/supabase-js';
import { getDbSupabase } from './supabase';
import { formatSupabaseError } from './formatSupabaseError';
import { insertAccountCreationChangeLog } from './accountCreationChangeLog';
import { PAYMENT_TERMS_LABEL } from './accountCreationUi';
import type {
  AccountCreationFinanceData,
  AccountCreationFormData,
  ApplicationSubmission,
  PaymentTermsOption,
} from '../types/applicationForm';

export type NavSyncStatus = 'pending' | 'success' | 'failed';

export type NavCustomerCardPayload = Record<string, string | number | boolean>;

export type PushAccountCreationToNavResult = {
  ok: boolean;
  error?: string;
  customer?: Record<string, unknown> | null;
  /** True when customer already existed in NAV (re-push / verify). */
  exists?: boolean;
};

export type NavCustomerCardSection = {
  id: string;
  /** Spreadsheet heading, e.g. 基本 / 描述 */
  title: string;
  subtitle: string;
  fields: readonly string[];
};

/**
 * Full CustomerCard catalogue by spreadsheet category — shown on Detail and used for push.
 * Blank values are omitted on POST so NAV defaults / triggers can run.
 */
export const NAV_CUSTOMER_CARD_SECTIONS: readonly NavCustomerCardSection[] = [
  {
    id: 'basic',
    title: '基本 / 描述',
    subtitle: 'Basic',
    fields: [
      'No',
      'Name',
      'Name_2',
      'Address',
      'Address_2',
      'Post_Code',
      'City',
      'County',
      'Country_Region_Code',
      'Search_Name',
      'Phone_No',
      'Fax_No',
      'E_Mail',
      'Home_Page',
      'Primary_Contact_No',
      'Contact',
    ],
  },
  {
    id: 'status',
    title: '状态 / 控制',
    subtitle: 'Status / Control',
    fields: [
      'Status',
      'Blocked',
      'Is_Active',
      'Password',
      'E_Commerce',
      'Registered',
      'Last_Date_Modified',
      'Created_By_UserId',
      'Creation_Date',
    ],
  },
  {
    id: 'sales',
    title: '销售 / 区域',
    subtitle: 'Sales / CRM',
    fields: [
      'Salesperson_Code',
      'Responsibility_Center',
      'Service_Zone_Code',
      'Territory_Code',
      'Trading_Terms',
      'Revision',
      'Cons_Reference_No',
      'Cons_Inv_Mail',
      'AR_Reference',
      'Session',
    ],
  },
  {
    id: 'motherOutlet',
    title: 'Mother / Outlet',
    subtitle: 'Mother / Outlet',
    fields: ['Mother_Outlet', 'Mother_Outlet_Name', 'Mother_Outlet_Price', 'Bill_to_Customer_No'],
  },
  {
    id: 'balance',
    title: '金额 / 限制',
    subtitle: 'Balance / Limit',
    fields: [
      'Balance_LCY',
      'Credit_Limit_LCY',
      'Post_Dated_Checks_LCY',
      'Balance_LCY_With_Post_Dated_Checks_LCY',
      'Minimum_Order_Amount',
    ],
  },
  {
    id: 'posting',
    title: '财务群组',
    subtitle: 'Posting Groups',
    fields: [
      'Gen_Bus_Posting_Group',
      'VAT_Bus_Posting_Group',
      'WHT_Business_Posting_Group',
      'Customer_Posting_Group',
      'Customer_Price_Group',
      'Customer_Disc_Group',
      'Global_Dimension_1_Code',
      'Global_Dimension_2_Code',
    ],
  },
  {
    id: 'payments',
    title: '付款',
    subtitle: 'Payments',
    fields: [
      'Payment_Terms_Code',
      'Payment_Method_Code',
      'Application_Method',
      'Partner_Type',
      'Reminder_Terms_Code',
      'Fin_Charge_Terms_Code',
      'Cash_Flow_Payment_Terms_Code',
      'Preferred_Bank_Account',
      'Block_Payment_Tolerance',
      'Print_Statements',
      'Last_Statement_No',
      'Last_Statement_Date',
      'Prepayment_Percent',
      'Allow_Line_Disc',
      'Prices_Including_VAT',
      'Invoice_Disc_Code',
    ],
  },
  {
    id: 'tax',
    title: '登记 / 注册',
    subtitle: 'Tax / Registration',
    fields: ['VAT_Registration_No', 'ABN', 'ABN_Division_Part_No', 'GLN', 'Tax_Document_Type'],
  },
  {
    id: 'shipping',
    title: '出货',
    subtitle: 'Shipping',
    fields: [
      'Delivery_Schedule',
      'Delivery_Instruction',
      'Location_Code',
      'Combine_Shipments',
      'Reserve',
      'Shipping_Advice',
      'Shipment_Method_Code',
      'Shipping_Agent_Code',
      'Shipping_Agent_Service_Code',
      'Shipping_Time',
      'Base_Calendar_Code',
      'Customized_Calendar',
      'Ready_Time',
      'Due_Time',
      'Init_Qty_To_Ship',
      'Init_Qty_To_Invoice',
    ],
  },
  {
    id: 'picContact',
    title: '丰购联系人',
    subtitle: 'Contact Person',
    fields: ['Pic_Phone_No', 'Pic_Fax_No', 'Pic_Email', 'Pic_Contact_Person'],
  },
  {
    id: 'others',
    title: '其它',
    subtitle: 'Others',
    fields: [
      'Currency_Code',
      'Language_Code',
      'IC_Partner_Code',
      'Document_Sending_Profile',
      'Invoice_Copies',
      'Printing_Period',
      'Copy_Sell_to_Addr_to_Qte_From',
      'Global_Dimension_1_Filter',
      'Global_Dimension_2_Filter',
      'Currency_Filter',
      'BTag',
    ],
  },
] as const;

/** Flat list of every CustomerCard field in catalogue order. */
export const NAV_CUSTOMER_CARD_FIELDS: readonly string[] = NAV_CUSTOMER_CARD_SECTIONS.flatMap(
  (s) => [...s.fields],
);

/** @deprecated Use NAV_CUSTOMER_CARD_SECTIONS */
export const NAV_CUSTOMER_CARD_FIELD_CATEGORIES = Object.fromEntries(
  NAV_CUSTOMER_CARD_SECTIONS.map((s) => [s.id, s.fields]),
) as Record<string, readonly string[]>;

/** Portal payment_terms → NAV Payment_Terms_Code (adjust if NAV codes differ). */
const PAYMENT_TERMS_TO_NAV: Record<PaymentTermsOption, string> = {
  cod: 'COD',
  '30_days': '30 DAYS',
  '45_days': '45 DAYS',
  '60_days': '60 DAYS',
  paynow: 'PAYNOW',
  cod_1: 'COD-1',
  cod_3: 'COD-3',
};

function joinAddressParts(...parts: Array<string | null | undefined>): string {
  return parts
    .map((p) => (p ?? '').trim())
    .filter(Boolean)
    .join(', ')
    .slice(0, 100);
}

function paymentTermsCode(value: string | null | undefined): string {
  if (!value) return '';
  if (value in PAYMENT_TERMS_TO_NAV) {
    return PAYMENT_TERMS_TO_NAV[value as PaymentTermsOption];
  }
  return PAYMENT_TERMS_LABEL[value as PaymentTermsOption] ?? value.trim();
}

/**
 * NAV CustomerCard stores cut-off in the customized `ABN` field as e.g. "12:00:00 PM".
 * Portal stores "12:00 PM" / "12:00:00 PM" — normalize to NAV style when possible.
 */
function navCutOffTime(value: string | null | undefined): string {
  const raw = (value ?? '').trim();
  if (!raw) return '';
  const m = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (!m) return raw.slice(0, 30);
  const hour = m[1].padStart(2, '0');
  const min = m[2];
  const sec = m[3] ?? '00';
  const ampm = m[4].toUpperCase();
  return `${hour}:${min}:${sec} ${ampm}`;
}

/**
 * Normalize portal schedule text to NAV style ("MON, WED & FRI") and optional
 * day-digit Delivery_Instruction ("135" = Mon/Wed/Fri), matching existing customers.
 */
export function normalizeNavDeliverySchedule(value: string | null | undefined): {
  schedule: string;
  dayDigits: string;
} {
  const raw = (value ?? '').trim();
  if (!raw) return { schedule: '', dayDigits: '' };

  const upper = raw.toUpperCase();
  if (/\b(EVERY\s*DAY|EVERYDAY|DAILY|ALL\s*DAYS?)\b/.test(upper)) {
    return { schedule: 'MON, TUES, WED, THURS, FRI', dayDigits: '12345' };
  }

  const days: Array<{ digit: string; label: string; re: RegExp }> = [
    { digit: '1', label: 'MON', re: /\bMON(?:DAY)?S?\b/i },
    { digit: '2', label: 'TUES', re: /\bTUE(?:S(?:DAY)?)?S?\b/i },
    { digit: '3', label: 'WED', re: /\bWED(?:NESDAY)?S?\b/i },
    { digit: '4', label: 'THURS', re: /\bTHU(?:R(?:S(?:DAY)?)?)?S?\b/i },
    { digit: '5', label: 'FRI', re: /\bFRI(?:DAY)?S?\b/i },
    { digit: '6', label: 'SAT', re: /\bSAT(?:URDAY)?S?\b/i },
    { digit: '7', label: 'SUN', re: /\bSUN(?:DAY)?S?\b/i },
  ];

  const hit = days.filter((d) => d.re.test(raw));
  if (hit.length === 0) {
    return { schedule: upper.slice(0, 50), dayDigits: '' };
  }

  const labels = hit.map((d) => d.label);
  let schedule: string;
  if (labels.length === 1) schedule = labels[0];
  else if (labels.length === 2) schedule = `${labels[0]}, ${labels[1]}`;
  else {
    schedule = `${labels.slice(0, -1).join(', ')} & ${labels[labels.length - 1]}`;
  }

  return {
    schedule: schedule.slice(0, 50),
    dayDigits: hit.map((d) => d.digit).join(''),
  };
}

function setIf(
  payload: NavCustomerCardPayload,
  key: string,
  value: string | number | boolean | null | undefined,
  maxLen?: number,
): void {
  if (value === null || value === undefined) return;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return;
    payload[key] = maxLen ? trimmed.slice(0, maxLen) : trimmed;
    return;
  }
  payload[key] = value;
}

/**
 * Auto-mapped defaults from portal form + finance (before Detail overrides in nav_card).
 */
export function deriveNavCardDefaults(row: ApplicationSubmission): Record<string, string> {
  const form = row.form_data as AccountCreationFormData;
  const finance = (row.finance_data ?? {}) as AccountCreationFinanceData;
  const no = row.customer_code?.trim() || '';
  const name = row.company_name?.trim() || '';

  const address = joinAddressParts(form.billing_street, form.billing_building);
  const address2 = joinAddressParts(form.billing_unit);
  const salesperson =
    finance.salesperson_code?.trim() || form.salesperson_code?.trim() || '';
  const { schedule: deliverySchedule, dayDigits } = normalizeNavDeliverySchedule(
    finance.delivery_schedule,
  );
  const session = (finance.session ?? '').trim().toUpperCase();
  const cutOff = navCutOffTime(finance.cut_off_time);
  const territory = (finance.territory_code ?? '').trim();
  const outlet = (form.outlet_name ?? '').trim();
  const mother = (finance.mother_account_code ?? '').trim();
  const outletCode = (finance.outlet_code ?? '').trim();
  const division = (finance.division_code ?? '').trim() || '10';

  const defaults: Record<string, string> = {
    No: no,
    Name: name,
    Name_2: outlet && outlet.toLowerCase() !== name.toLowerCase() ? outlet : '',
    Address: address,
    Address_2: address2,
    Post_Code: (form.billing_postal ?? '').trim(),
    Country_Region_Code: 'SG',
    Search_Name: name,
    Phone_No: (form.finance_contact_no ?? '').trim(),
    E_Mail: (form.finance_email ?? '').trim(),
    Contact: (form.finance_contact_name ?? '').trim(),
    Salesperson_Code: salesperson,
    Territory_Code: territory.length <= 10 ? territory : '',
    Session: session === 'AM' || session === 'PM' ? session : '',
    Mother_Outlet: outletCode || mother,
    Bill_to_Customer_No: mother,
    Customer_Price_Group: (finance.customer_price_group ?? '').trim(),
    Global_Dimension_2_Code: division,
    Payment_Terms_Code: paymentTermsCode(finance.payment_terms),
    VAT_Registration_No: (form.gst_reg_no ?? '').trim(),
    ABN: cutOff,
    GLN: (form.company_reg_no ?? '').trim(),
    Delivery_Schedule: deliverySchedule,
    Delivery_Instruction: dayDigits,
    Pic_Phone_No: (form.sales_contact_no ?? '').trim(),
    Pic_Email: (form.sales_email ?? '').trim(),
    Pic_Contact_Person: (form.sales_contact_name ?? '').trim(),
  };

  return defaults;
}

/** Effective value shown on Detail / sent on push (override wins over auto default). */
export function resolveNavCardValue(
  key: string,
  overrides: Record<string, string> | null | undefined,
  defaults: Record<string, string>,
): string {
  if (overrides && Object.prototype.hasOwnProperty.call(overrides, key)) {
    return overrides[key] ?? '';
  }
  return defaults[key] ?? '';
}

/**
 * Build CustomerCard JSON body from an approved Account Creation row.
 * Auto-mapped defaults + finance.nav_card Detail overrides; empty strings omitted.
 */
export function buildNavCustomerCardPayload(
  row: ApplicationSubmission,
): NavCustomerCardPayload {
  const form = row.form_data as AccountCreationFormData;
  const finance = (row.finance_data ?? {}) as AccountCreationFinanceData;
  const defaults = deriveNavCardDefaults(row);
  /** New Application stores on form_data; Detail edits prefer finance.nav_card. */
  const overrides = {
    ...(form.nav_card ?? {}),
    ...(finance.nav_card ?? {}),
  };
  const payload: NavCustomerCardPayload = {};

  for (const key of NAV_CUSTOMER_CARD_FIELDS) {
    setIf(payload, key, resolveNavCardValue(key, overrides, defaults));
  }

  return payload;
}

async function readFunctionsHttpError(error: FunctionsHttpError): Promise<string> {
  try {
    const response = error.context;
    if (response && typeof response.json === 'function') {
      const body = (await response.json()) as { error?: string; message?: string };
      if (typeof body.error === 'string' && body.error.trim()) return body.error;
      if (typeof body.message === 'string' && body.message.trim()) return body.message;
    }
  } catch {
    /* fall through */
  }
  return error.message || 'Edge Function returned a non-2xx status code';
}

/** Call Supabase Edge Function `nav-odata` (avoids Vodien shared-host outbound :7048 block). */
async function callNavOdataProxy(body: Record<string, unknown>): Promise<{
  ok: boolean;
  error?: string;
  customer?: Record<string, unknown> | null;
  exists?: boolean;
}> {
  const { data, error } = await getDbSupabase('forms').functions.invoke('nav-odata', {
    body,
  });

  if (error) {
    if (error instanceof FunctionsHttpError) {
      return { ok: false, error: await readFunctionsHttpError(error) };
    }
    return { ok: false, error: error.message || 'NAV Edge Function call failed.' };
  }

  const payload = (data ?? {}) as {
    ok?: boolean;
    error?: string;
    customer?: Record<string, unknown> | null;
    exists?: boolean;
  };

  if (payload.ok === false || payload.error) {
    return {
      ok: false,
      error: payload.error || 'NAV push failed.',
      exists: payload.exists,
      customer: payload.customer ?? null,
    };
  }

  return {
    ok: true,
    customer: payload.customer ?? null,
    exists: payload.exists,
  };
}

export async function updateAccountCreationNavSync(
  submissionId: string,
  input: {
    status: NavSyncStatus;
    error?: string | null;
  },
): Promise<void> {
  const db = getDbSupabase('forms');
  const { error } = await db
    .from('account_creation_submissions')
    .update({
      nav_sync_status: input.status,
      nav_synced_at: new Date().toISOString(),
      nav_sync_error: input.error?.trim() || null,
    })
    .eq('id', submissionId)
    .eq('status', 'approved');
  if (error) throw new Error(formatSupabaseError(error));
}

/** Persist Detail NAV field overrides onto finance_data.nav_card (approved rows). */
export async function saveAccountCreationNavCard(
  submissionId: string,
  financeData: AccountCreationFinanceData,
): Promise<void> {
  const db = getDbSupabase('forms');
  const { error } = await db
    .from('account_creation_submissions')
    .update({ finance_data: financeData })
    .eq('id', submissionId)
    .eq('status', 'approved');
  if (error) throw new Error(formatSupabaseError(error));
}

/**
 * Push approved Account Creation to NAV CustomerCard (manual).
 * Keeps Portal status = approved; records nav_sync_* + change log.
 */
export async function pushAccountCreationToNav(
  row: ApplicationSubmission,
  actor?: string | null,
  overrides?: {
    financeData?: AccountCreationFinanceData | null;
    formData?: AccountCreationFormData | null;
    customerCode?: string | null;
    companyName?: string | null;
  },
): Promise<PushAccountCreationToNavResult> {
  const effective: ApplicationSubmission = {
    ...row,
    company_name: overrides?.companyName?.trim() || row.company_name,
    customer_code: overrides?.customerCode?.trim() || row.customer_code,
    form_data: {
      ...(row.form_data as AccountCreationFormData),
      ...(overrides?.formData ?? {}),
    },
    finance_data: {
      ...((row.finance_data ?? {}) as AccountCreationFinanceData),
      ...(overrides?.financeData ?? {}),
    },
  };

  if (effective.status !== 'approved') {
    return { ok: false, error: 'Only approved applications can be pushed to NAV.' };
  }
  const code = effective.customer_code?.trim();
  if (!code) {
    return { ok: false, error: 'Customer code is required before pushing to NAV.' };
  }
  if (!effective.company_name?.trim()) {
    return { ok: false, error: 'Company name is required before pushing to NAV.' };
  }
  const form = effective.form_data as AccountCreationFormData;
  const finance = (effective.finance_data ?? {}) as AccountCreationFinanceData;
  const defaults = deriveNavCardDefaults(effective);
  const navOverrides = {
    ...(form.nav_card ?? {}),
    ...(finance.nav_card ?? {}),
  };
  const deliverySchedule = resolveNavCardValue(
    'Delivery_Schedule',
    navOverrides,
    defaults,
  );
  if (!deliverySchedule.trim() && !finance.delivery_schedule?.trim()) {
    return {
      ok: false,
      error:
        'Delivery schedule is required before pushing to NAV (NAV: select at least one delivery day).',
    };
  }
  const territory =
    resolveNavCardValue('Territory_Code', navOverrides, defaults).trim() ||
    finance.territory_code?.trim() ||
    '';
  if (territory.length > 10) {
    return {
      ok: false,
      error: `Territory code must be ≤ 10 characters for NAV (got "${territory}"). Use a NAV code such as SOUTHEAST or SOUTH 1.`,
    };
  }

  // Persist Detail overrides before push so reload keeps them.
  try {
    await saveAccountCreationNavCard(effective.id, finance);
  } catch {
    /* non-blocking — still attempt NAV create */
  }

  const customer = buildNavCustomerCardPayload(effective);
  const result = await callNavOdataProxy({
    action: 'createCustomer',
    customer,
  });

  if (!result.ok) {
    try {
      await updateAccountCreationNavSync(effective.id, {
        status: 'failed',
        error: result.error ?? 'NAV push failed.',
      });
      await insertAccountCreationChangeLog({
        submissionId: effective.id,
        changedBy: actor,
        changeType: 'nav_push',
        summary: `NAV push failed: ${result.error ?? 'unknown error'}`,
        beforeJson: { nav_sync_status: effective.nav_sync_status ?? null },
        afterJson: { nav_sync_status: 'failed', customer },
      });
    } catch {
      /* non-blocking */
    }
    return { ok: false, error: result.error ?? 'NAV push failed.' };
  }

  try {
    await updateAccountCreationNavSync(effective.id, { status: 'success', error: null });
    await insertAccountCreationChangeLog({
      submissionId: effective.id,
      changedBy: actor,
      changeType: 'nav_push',
      summary: `Pushed to NAV Customer ${code}`,
      beforeJson: { nav_sync_status: effective.nav_sync_status ?? null },
      afterJson: {
        nav_sync_status: 'success',
        customer_no: code,
        nav_customer: result.customer ?? null,
      },
    });
  } catch (err) {
    return {
      ok: false,
      error:
        err instanceof Error
          ? `Created in NAV but failed to save sync status: ${err.message}`
          : 'Created in NAV but failed to save sync status.',
      customer: result.customer,
    };
  }

  return { ok: true, customer: result.customer };
}
